import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API_BASE });

// Attach JWT token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post("/auth/register", data),
  login: (data) => api.post("/auth/login", data),
  me: () => api.get("/auth/me"),
};

// Restaurant
export const restaurantAPI = {
  setup: (data) => api.post("/restaurant/setup", data),
  get: () => api.get("/restaurant"),
  updateHours: (hours) => api.put("/restaurant/hours", { hours }),
  connectPOS: (data) => api.post("/restaurant/pos-connect", data),
  searchNumbers: (areaCode) => api.post("/restaurant/search-numbers", { area_code: areaCode || "" }),
  provisionPhone: (phoneNumber) => api.post("/restaurant/provision-phone", null, { params: phoneNumber ? { phone_number: phoneNumber } : {} }),
  activate: () => api.post("/restaurant/activate"),
  updateSettings: (data) => api.put("/restaurant/settings", data),
};

// SMS
export const smsAPI = {
  sendConfirmation: (data) => api.post("/sms/send-confirmation", data),
};

// Menu
export const menuAPI = {
  getCategories: () => api.get("/menu/categories"),
  createCategory: (data) => api.post("/menu/categories", data),
  deleteCategory: (id) => api.delete(`/menu/categories/${id}`),
  getItems: (categoryId) => api.get("/menu/items", { params: categoryId ? { category_id: categoryId } : {} }),
  createItem: (data) => api.post("/menu/items", data),
  updateItem: (id, data) => api.put(`/menu/items/${id}`, data),
  deleteItem: (id) => api.delete(`/menu/items/${id}`),
  syncPOS: () => api.post("/menu/sync-pos"),
  syncStatus: () => api.get("/menu/sync-status"),
  aiParse: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/menu/ai-parse", formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
  },
  aiImport: (data) => api.post("/menu/ai-import", data),
  aiSuggestions: () => api.post("/menu/ai-suggestions"),
};

// Calls
export const callsAPI = {
  list: (params) => api.get("/calls", { params }),
  detail: (id) => api.get(`/calls/${id}`),
  aiSummary: (id) => api.post(`/calls/${id}/ai-summary`),
  live: () => api.get("/calls/live"),
  liveTranscript: (id) => api.get(`/calls/${id}/live-transcript`),
  recordings: (id) => api.get(`/calls/${id}/recordings`),
  listenIn: (callId, phone) => api.post("/calls/listen-in", { call_id: callId, listener_phone: phone }),
};

// Orders
export const ordersAPI = {
  list: (params) => api.get("/orders", { params }),
  detail: (id) => api.get(`/orders/${id}`),
};

// Dashboard
export const dashboardAPI = {
  metrics: () => api.get("/dashboard/metrics"),
  recentCalls: () => api.get("/dashboard/recent-calls"),
  weeklyTrends: () => api.get("/dashboard/weekly-trends"),
  aiInsights: () => api.get("/dashboard/ai-insights"),
};

// AI
export const aiAPI = {
  generateGreeting: (data) => api.post("/ai/generate-greeting", data),
};

// Knowledge Graph
export const knowledgeAPI = {
  list: () => api.get("/knowledge"),
  create: (data) => api.post("/knowledge", data),
  update: (id, data) => api.put(`/knowledge/${id}`, data),
  delete: (id) => api.delete(`/knowledge/${id}`),
  aiGenerate: () => api.post("/knowledge/ai-generate"),
};

// Upsell Engine
export const upsellAPI = {
  getConfig: () => api.get("/upsell/config"),
  updateConfig: (data) => api.put("/upsell/config", data),
  getMetrics: () => api.get("/upsell/metrics"),
};

// Ops / Admin
export const opsAPI = {
  stats: () => api.get("/ops/stats"),
  listUsers: (params) => api.get("/ops/users", { params }),
  getUser: (id) => api.get(`/ops/users/${id}`),
  editUser: (id, data) => api.put(`/ops/users/${id}`, data),
  suspendUser: (id) => api.post(`/ops/users/${id}/suspend`),
  activateUser: (id) => api.post(`/ops/users/${id}/activate`),
  deleteUser: (id) => api.delete(`/ops/users/${id}`),
  updateBilling: (id, data) => api.put(`/ops/users/${id}/billing`, data),
  grantComp: (id, data) => api.post(`/ops/users/${id}/comp`, data),
  impersonate: (id) => api.post(`/ops/users/${id}/impersonate`),
  activityLogs: (params) => api.get("/ops/activity", { params }),
  // Restaurant management
  getRestaurantData: (rid, collection) => api.get(`/ops/restaurants/${rid}/data/${collection}`),
  updateRestaurant: (rid, data) => api.put(`/ops/restaurants/${rid}`, data),
  createMenuCategory: (rid, data) => api.post(`/ops/restaurants/${rid}/menu-categories`, data),
  createMenuItem: (rid, data) => api.post(`/ops/restaurants/${rid}/menu-items`, data),
  updateMenuItem: (rid, itemId, data) => api.put(`/ops/restaurants/${rid}/menu-items/${itemId}`, data),
  deleteRestaurantMenuItem: (rid, itemId) => api.delete(`/ops/restaurants/${rid}/menu-items/${itemId}`),
  deleteAllMenuItems: (rid) => api.delete(`/ops/restaurants/${rid}/menu-items-all`),
  deleteRestaurantKnowledge: (rid, entryId) => api.delete(`/ops/restaurants/${rid}/knowledge/${entryId}`),
  deleteAllKnowledge: (rid) => api.delete(`/ops/restaurants/${rid}/knowledge-all`),
  regenerateKnowledge: (rid) => api.post(`/ops/restaurants/${rid}/knowledge-regenerate`),
  createRestaurant: (data) => api.post("/ops/restaurants/create", data),
};

// Seed
export const seedAPI = {
  seedDemo: () => api.post("/seed-demo-data"),
};

export default api;
